import { useHistory, useLocation } from 'react-router-dom';
import { Col, Row, Spin } from 'antd';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { useEffect } from 'react';

import { pxToNumber } from '../../utils/cssUtils';
import { cssVariables } from '../../styles/cssVariables';
import { GET_INVOICE_TRANSACTION_HISTORY_QUERY } from '../../app/graphql/invoice';
import DynamicTable from '../../components/tables/table';
import { useDynamicTablePageSize } from './invoiceUtils/useDynamicTablePageSize';
import { useInvoiceTransactionsTable } from './invoiceUtils/useInvoiceTransactionsTable';
import { EMPTY_VALUE } from '../../common/utils/formatUtils';
import { CurrencyFormat } from '../../components/domainSpecific/currencyElements';
import { useFlashMessageContext } from '../../components/dialogs/FlashMessageProvider';
import InvoiceContainer from './InvoiceContainer';
import { useInvoiceStyles } from './invoice.styles';

const getSummaryBoxColmuns = (invoiceTransactionHistory, invoiceCurrency) => [
  {
    key: 'invoiceDate',
    value: moment(invoiceTransactionHistory?.invoiceDate, 'MM/DD/YYYY').format(
      'MMM-DD-YYYY'
    ),
  },
  {
    key: 'invoiceTotal',
    value: (
      <CurrencyFormat
        value={Number(invoiceTransactionHistory?.invoiceTotal)}
        currency={invoiceCurrency || 'USD'}
      />
    ),
  },
  {
    key: 'invoiceOpenBalance',
    value: (
      <CurrencyFormat
        value={Number(invoiceTransactionHistory?.invoiceOpenBalance)}
        currency={invoiceCurrency || 'USD'}
      />
    ),
  },
];

export default function InvoiceTransactionHistory() {
  const history = useHistory();
  const location = useLocation();
  const { state: invoiceParams } = location || {};
  const { errorMessage } = useFlashMessageContext();
  const { styles } = useInvoiceStyles();
  const { containerRef, pageSize } = useDynamicTablePageSize();
  const { data, loading, error } = useQuery(
    GET_INVOICE_TRANSACTION_HISTORY_QUERY,
    {
      variables: {
        invoiceNumber: invoiceParams?.invoiceNumber,
      },
      skip: !invoiceParams,
    }
  );
  const { getInvoiceTransactionHistory: invoiceTransactionHistory } =
    data || {};

  const { invoiceTransactionsColumns, invoiceTransactionsTableData } =
    useInvoiceTransactionsTable(
      invoiceTransactionHistory,
      invoiceParams?.invoiceCurrency
    );

  const handleBack = () => {
    history.goBack();
  };

  useEffect(() => {
    if (error) {
      errorMessage({ contentId: 'error.appError' });
      history.goBack();
    } else if (!loading && !invoiceTransactionHistory) {
      errorMessage({ contentId: 'invoice.transactionHistory.noData' });
      history.goBack();
    }
  }, [invoiceTransactionHistory, error, loading, errorMessage, history]);

  if (loading || !invoiceParams) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <InvoiceContainer
      handleBack={handleBack}
      titleId="invoice.transactionHistory"
    >
      <div className={styles.textContainer}>
        <div className={styles.textWrapper}>
          <div className={styles.number}>
            <span className={styles.numberText}>
              <FormattedMessage id="invoice.invoiceNumber" />
            </span>
            <span className={styles.numberValue}>
              {invoiceParams?.invoiceNumber}
            </span>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          styles.summaryBox,
          styles.transactionHistorySummaryBox
        )}
      >
        <Row gutter={pxToNumber(cssVariables.spaceNorm2_5)}>
          {getSummaryBoxColmuns(
            invoiceTransactionHistory,
            invoiceParams?.invoiceCurrency
          ).map(item => (
            <Col xs={24} sm={12} md={8} lg={8} key={item.key}>
              <div className={styles.summaryItem}>
                <div className="title">
                  <FormattedMessage id={`invoice.${item.key}`} />
                </div>
                <div className="value text-uppercase">{item.value}</div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
      <div
        className={classNames('text-center', styles.transactionHistoryLegend)}
      >
        <FormattedMessage id="invoice.transactionHistory.legend" />
      </div>
      <div className={styles.tableContainer} ref={containerRef}>
        <DynamicTable
          columns={invoiceTransactionsColumns}
          data={invoiceTransactionsTableData}
          pagination={{
            pageSize,
          }}
          emptyText={EMPTY_VALUE}
          rowClassName={() => styles.transactionHistoryTableRow}
        />
      </div>
    </InvoiceContainer>
  );
}
